import { hasOpenAnswers } from "./OpenAnswers";
import { v4 as uuidv4 } from "uuid";
import { LocalizedStringRecord } from "../app/slices/TrainingLocalizationSlice";
import {
  getInteractionFromFactory,
  ReconcileContext,
} from "../features/interaction_factory/InteractionFactory";
import { copyInteractionWithLocalizationFallback } from "../helpers/InteractionCopyHelper";
import { Actor } from "./Actor";
import { Prop } from "./Prop";
import { IAnswer, IFormQuiz, IQuizFeedback, QuizFeedbackSlot } from "./Quiz";
import Take from "./Take";
import Interaction, { InteractionType } from "./interactions/Interaction";

// Quizzes that support correct/wrong feedback interactions. Adding a future
// quiz type here (plus the helper calls in its factory entity) is all that's
// needed to enable the feature for it.
export const QUIZ_TYPES_WITH_FEEDBACK: ReadonlySet<InteractionType> = new Set([
  InteractionType.MultipleChoiceQuiz,
  InteractionType.MultipleChoiceGraphicsQuiz,
  InteractionType.DialogQuiz,
]);

export function supportsQuizFeedback(
  interaction: Interaction | undefined
): boolean {
  return (
    interaction !== undefined && QUIZ_TYPES_WITH_FEEDBACK.has(interaction.type)
  );
}

export function isQuizFeedbackEnabled(interaction: Interaction | undefined): boolean {
  return supportsQuizFeedback(interaction) && !hasOpenAnswers(interaction);
}

// Interaction types allowed inside a feedback slot. These must stay in sync
// with the picker subsets (FeedbackSelectableDefinitions360/3D) — a parity
// test in InteractionDefinitions guards against drift. Quizzes, markers, and
// flow control are deliberately absent so feedback can never nest a quiz.
export const FEEDBACK_ELIGIBLE_TYPES_360: ReadonlySet<InteractionType> =
  new Set([
    InteractionType.MessagePopup,
    InteractionType.LisaMessage,
    InteractionType.ImagePopup,
    InteractionType.VideoPopup,
    InteractionType.PdfPopup,
    InteractionType.AudioPopup,
    InteractionType.Slideshow,
  ]);

export const FEEDBACK_ELIGIBLE_TYPES_3D: ReadonlySet<InteractionType> =
  new Set([
    ...FEEDBACK_ELIGIBLE_TYPES_360,
    InteractionType.Conversation,
    InteractionType.MessageBubble,
    InteractionType.EmotionBubble,
    InteractionType.Sequence,
  ]);

export function getFeedbackEligibleTypes(
  is3D: boolean
): ReadonlySet<InteractionType> {
  return is3D ? FEEDBACK_ELIGIBLE_TYPES_3D : FEEDBACK_ELIGIBLE_TYPES_360;
}

export function getFeedbackIneligibleInteractions(
  interactions: Interaction[],
  eligibleTypes: ReadonlySet<InteractionType>
): Interaction[] {
  return interactions.filter(
    (interaction) => !eligibleTypes.has(interaction.type)
  );
}

export function areInteractionsFeedbackEligible(
  interactions: Interaction[],
  eligibleTypes: ReadonlySet<InteractionType>
): boolean {
  return (
    getFeedbackIneligibleInteractions(interactions, eligibleTypes).length === 0
  );
}

export function getFeedbackArray(
  quiz: IQuizFeedback,
  slot: QuizFeedbackSlot
): Interaction[] {
  return (
    (slot === "correct"
      ? quiz.correctFeedbackInteractions
      : quiz.wrongFeedbackInteractions) ?? []
  );
}

export interface QuizFeedbackCounts {
  correct: number;
  wrong: number;
}

// Returns undefined when the interaction can't hold feedback or holds none, so
// callers get a single truthiness check plus the counts in one call.
export function getQuizFeedbackCounts(
  interaction: Interaction | undefined
): QuizFeedbackCounts | undefined {
  if (!isQuizFeedbackEnabled(interaction)) return undefined;
  const quiz = interaction as IFormQuiz;
  const correct = getFeedbackArray(quiz, "correct").length;
  const wrong = getFeedbackArray(quiz, "wrong").length;
  if (correct === 0 && wrong === 0) return undefined;
  return { correct, wrong };
}

export function createEmptyQuizFeedback(): Required<IQuizFeedback> {
  return {
    correctFeedbackInteractions: [],
    wrongFeedbackInteractions: [],
  };
}

export function migrateQuizFeedback(
  from: number,
  to: number,
  quiz: IQuizFeedback
): Required<IQuizFeedback> {
  const migrateArray = (interactions: Interaction[] | undefined) =>
    (interactions ?? []).map((interaction) => {
      const factoryEntity = getInteractionFromFactory(interaction.type);
      return factoryEntity
        ? factoryEntity.Migrate(from, to, interaction)
        : interaction;
    });

  return {
    correctFeedbackInteractions: migrateArray(
      quiz.correctFeedbackInteractions
    ),
    wrongFeedbackInteractions: migrateArray(quiz.wrongFeedbackInteractions),
  };
}

export function reconcileQuizFeedback<T extends Interaction & IQuizFeedback>(
  quiz: T,
  context: ReconcileContext
): T {
  const reconcileArray = (interactions: Interaction[] | undefined) => {
    if (!interactions?.length) return interactions;
    let changed = false;
    const result = interactions.map((interaction) => {
      const factoryEntity = getInteractionFromFactory(interaction.type);
      const reconciled = factoryEntity?.Reconcile
        ? factoryEntity.Reconcile(interaction, context)
        : interaction;
      if (reconciled !== interaction) changed = true;
      return reconciled;
    });
    return changed ? result : interactions;
  };

  const correct = reconcileArray(quiz.correctFeedbackInteractions);
  const wrong = reconcileArray(quiz.wrongFeedbackInteractions);
  if (
    correct === quiz.correctFeedbackInteractions &&
    wrong === quiz.wrongFeedbackInteractions
  )
    return quiz;

  return {
    ...quiz,
    correctFeedbackInteractions: correct,
    wrongFeedbackInteractions: wrong,
  };
}

export function copyQuizFeedback(
  quiz: IQuizFeedback,
  getKeys: (amount: number) => string[],
  replaceKey: (from: string, to: string) => void,
  getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]
): Required<IQuizFeedback> {
  const copyArray = (interactions: Interaction[] | undefined) =>
    (interactions ?? []).map((interaction) => {
      const factoryEntity = getInteractionFromFactory(interaction.type);
      if (factoryEntity?.Copy)
        return factoryEntity.Copy(
          interaction,
          getKeys,
          replaceKey,
          getAnswerArray
        );

      return copyInteractionWithLocalizationFallback(
        interaction,
        getKeys,
        replaceKey,
        uuidv4
      );
    });

  return {
    correctFeedbackInteractions: copyArray(quiz.correctFeedbackInteractions),
    wrongFeedbackInteractions: copyArray(quiz.wrongFeedbackInteractions),
  };
}

const slotLabels: Record<QuizFeedbackSlot, string> = {
  correct: "Correct",
  wrong: "Wrong",
};

export function validateQuizFeedback(
  quiz: IQuizFeedback & Pick<Interaction, "properties">,
  getValue: (key: string) => string,
  take: Take,
  actorPresets: Actor[]
): string[] {
  if (hasOpenAnswers(quiz)) return [];
  const warnings: string[] = [];

  const slots: QuizFeedbackSlot[] = ["correct", "wrong"];
  slots.forEach((slot) => {
    getFeedbackArray(quiz, slot).forEach((interaction) => {
      const factoryEntity = getInteractionFromFactory(interaction.type);
      const nestedWarnings =
        factoryEntity?.Validate?.(interaction, getValue, take, actorPresets) ??
        [];
      nestedWarnings.forEach((warning) =>
        warnings.push(
          `Feedback (${slotLabels[slot]}) "${interaction.name}": ${warning}`
        )
      );
    });
  });

  return warnings;
}

export function validateQuizFeedbackErrors(
  quiz: IQuizFeedback & Pick<Interaction, "properties">,
  parentTake: Take,
  currentTake: Take,
  actorPresets: Actor[],
  allPropPresets: Prop[],
  localizedStringRecords: LocalizedStringRecord
): string[] {
  if (hasOpenAnswers(quiz)) return [];
  const errors: string[] = [];

  const slots: QuizFeedbackSlot[] = ["correct", "wrong"];
  slots.forEach((slot) => {
    getFeedbackArray(quiz, slot).forEach((interaction) => {
      const factoryEntity = getInteractionFromFactory(interaction.type);
      const nestedErrors =
        factoryEntity?.ValidateErrors?.(
          interaction,
          parentTake,
          currentTake,
          actorPresets,
          allPropPresets,
          localizedStringRecords
        ) ?? [];
      nestedErrors.forEach((error) => {
        const message = typeof error === "string" ? error : error.message;
        errors.push(
          `Feedback (${slotLabels[slot]}) "${interaction.name}": ${message}`
        );
      });
    });
  });

  return errors;
}

export function collectQuizFeedbackSearchableStrings(
  quiz: IQuizFeedback
): string[] {
  return [
    ...getFeedbackArray(quiz, "correct"),
    ...getFeedbackArray(quiz, "wrong"),
  ].map((interaction) => interaction.name);
}
